import { formatDate } from '@/shared/utils/dateUtils';

import type { IDeviceBilling, IDeviceDetails, IDeviceRentedProject } from '../types/device';

function formatRentedProjectLabel(project?: IDeviceRentedProject | null): string | null {
  if (!project?.project_name) {
    return null;
  }
  return project.code ? `${project.project_name} (${project.code})` : project.project_name;
}

function getDeviceRentedProject(device: IDeviceDetails): IDeviceRentedProject | null {
  return device.rented_project ?? device.rented_to?.project ?? null;
}

function formatMoney(amount: string | number | undefined, currency = 'USD'): string {
  if (amount === undefined || amount === null || amount === '') {
    return '—';
  }
  const symbol = currency === 'USD' ? '$' : `${currency} `;
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Number.isNaN(num)) {
    return String(amount);
  }
  return `${symbol}${num.toFixed(2)}`;
}

export function formatBillingRate(billing: IDeviceBilling): string {
  return `${formatMoney(billing.price_hourly || 0.1, billing.currency ?? 'USD')} / hr`;
}

function diffHours(start?: string | null, end?: Date): number {
  if (!start) {
    return 0;
  }
  const startDate = new Date(start);
  const endDate = end ?? new Date();
  if (Number.isNaN(startDate.getTime())) {
    return 0;
  }
  return Math.max(0, Math.round((endDate.getTime() - startDate.getTime()) / 3600000));
}

function diffDays(start?: string | null, end?: string | null): number {
  if (!start || !end) {
    return 0;
  }
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return 0;
  }
  return Math.max(0, Math.ceil((endDate.getTime() - startDate.getTime()) / 86400000));
}

export function getBillingDisplayFields(
  device: IDeviceDetails
): { label: string; value: string }[] {
  if (!device.rented_to || !device.rental_start_at) {
    return [];
  }

  const billing = device.billing;
  const currency = billing?.currency ?? 'USD';
  const hourlyRate = parseFloat(billing?.price_hourly || '0.10');

  const cutoff =
    device.device_status?.toLowerCase() === 'maintenance' && device.maintenance_started_at
      ? new Date(device.maintenance_started_at)
      : new Date();

  const computedHours = diffHours(device.rental_start_at, cutoff);
  const billableHours =
    (billing as { billable_hours?: number; hours_billed?: number } | undefined)?.billable_hours ??
    (billing as { hours_billed?: number } | undefined)?.hours_billed ??
    computedHours;

  const amountDue =
    (billing as { amount_due?: string | number } | undefined)?.amount_due ??
    (billing as { amount?: string | number } | undefined)?.amount;

  const rate = formatBillingRate(billing ?? { price_hourly: '0.10', currency: 'USD' });
  const hours = `${billableHours} hrs`;
  const amount =
    amountDue !== undefined
      ? formatMoney(amountDue, currency)
      : formatMoney(
          (typeof billableHours === 'number' ? billableHours : computedHours) *
            (Number.isNaN(hourlyRate) ? 0.1 : hourlyRate),
          currency
        );

  return [
    { label: 'Rate', value: rate },
    { label: 'Hours Billed', value: hours },
    { label: 'Amount Due', value: amount },
  ];
}

export function getRentalDisplayFields(device: IDeviceDetails): { label: string; value: string }[] {
  if (!device.rented_to) {
    return [];
  }

  const fields: { label: string; value: string }[] = [
    { label: 'Rented To', value: device.rented_to.org_name },
  ];

  const projectLabel = formatRentedProjectLabel(getDeviceRentedProject(device));
  if (projectLabel) {
    fields.push({ label: 'Project', value: projectLabel });
  }

  fields.push(
    { label: 'Rental Start', value: formatDate(device.rental_start_at) || '—' },
    { label: 'Rental End', value: formatDate(device.rental_end_at) || '—' }
  );

  const status = device.device_status?.toLowerCase();
  if (status === 'rented' && device.rental_end_at) {
    const remaining = diffDays(new Date().toISOString(), device.rental_end_at);
    fields.push({ label: 'Remaining', value: `${remaining} days` });
  }

  if (status === 'maintenance') {
    fields.push({
      label: 'Note',
      value: `Reserved for ${device.rented_to.org_name} — rental paused during maintenance`,
    });
  }

  return fields;
}

export function isBillingPaused(device: IDeviceDetails): boolean {
  return Boolean(device.billing?.paused || device.device_status?.toLowerCase() === 'maintenance');
}

export function hasRentalSection(device: IDeviceDetails): boolean {
  return Boolean(device.rented_to);
}
